import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import checkSmallTokens from '@amzn/meridian-tokens/base/icon/check-small';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import { EvidencePhoto } from './model';
import { formatCheckScore, formatDetectedPii } from './imageQualityUtils';
import { useIqColors } from './meridianUi';

/** One scored evidence photo: image + failure badge, date, per-check ✓/✗, PII detections. */
export interface PhotoCardProps {
  photo: EvidencePhoto;
  /** The image link, from the page's batch request. */
  url?: string;
  loading?: boolean;
  /** ReVision has no image for this photo. */
  unavailable?: boolean;
  /** The image didn't load (usually an expired link). */
  onImageError?: () => void;
}

/** Capture time in the viewer's time zone (the site's, for leads at the site). */
const formatCaptured = (epochMs: number) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(epochMs));

const PhotoCard: React.FC<PhotoCardProps> = ({
  photo,
  url,
  loading,
  unavailable,
  onImageError,
}) => {
  const colors = useIqColors();
  // The link that last failed to load. It stays on the placeholder until a refetch brings a new link.
  const [failedUrl, setFailedUrl] = useState<string>();
  const imageFailed = !!url && url === failedUrl;
  const showImage = !!url && !imageFailed;
  const onLoadFailed = () => {
    setFailedUrl(url);
    onImageError?.();
  };
  const alt = photo.primaryFailure
    ? `${photo.primaryFailure.failureLabel} evidence photo`
    : 'Evidence photo';
  const badge = photo.primaryFailure && (
    <Box backgroundColor={colors.failure(photo.primaryFailure.checkId)} spacingInset="100 200">
      <Text type="h100" color="inverted">
        {photo.primaryFailure.failureLabel.toUpperCase()}
      </Text>
    </Box>
  );
  return (
    <Card spacingInset="none" data-testid={`iq-photo-${photo.photoId}`}>
      <div style={{ position: 'relative' }} data-testid={`iq-photo-${photo.photoId}-image`}>
        {showImage ? (
          // Plain img: Meridian Image has no onError, which we need to refetch an expired link.
          <img
            src={url}
            alt={alt}
            onError={onLoadFailed}
            style={{ aspectRatio: '4 / 3', width: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ aspectRatio: '4 / 3', background: colors.track, display: 'flex' }}>
            <Column alignmentHorizontal="center" alignmentVertical="center" width="100%">
              {loading ? (
                <Loader size="small" />
              ) : (
                <Text type="b200" color="secondary">
                  {unavailable || imageFailed ? 'Image not available' : ''}
                </Text>
              )}
            </Column>
          </div>
        )}
        {badge && <div style={{ position: 'absolute', top: 12, left: 12 }}>{badge}</div>}
      </div>
      <Box spacingInset="400">
        <Column spacing="300">
          <Text type="b300" color="secondary">
            {formatCaptured(photo.capturedAt)}
          </Text>
          {photo.checks.map(check => {
            const score = formatCheckScore(check);
            return (
              <Row
                key={check.checkId}
                spacing="300"
                alignmentHorizontal="justify"
                data-testid={`iq-photo-${photo.photoId}-${check.checkId}`}
                data-passed={check.passed}
              >
                <Row spacing="300">
                  <Text tag="span" color={check.passed ? 'success' : 'error'}>
                    <Icon tokens={check.passed ? checkSmallTokens : closeSmallTokens}>
                      {check.passed ? 'Passed' : 'Failed'}
                    </Icon>
                  </Text>
                  <Text type="b400">{check.label}</Text>
                </Row>
                {score && (
                  <Text type="b200" color="secondary">
                    {score}
                  </Text>
                )}
              </Row>
            );
          })}
          {!!photo.detected?.length && (
            <>
              <Divider />
              <Text type="b300" color="secondary">
                {formatDetectedPii(photo.detected)}
              </Text>
            </>
          )}
        </Column>
      </Box>
    </Card>
  );
};

export default PhotoCard;
